import { useState } from 'react';

interface BatchPricingModalProps {
  selectedCount: number;
  onClose: () => void;
  onSave: (data: {
    value: number;
    estimate_days: number;
    billing_category: string;
    mode: 'individual' | 'total';
  }) => void;
}

const BILLING_CATEGORIES = [
  { value: 'Desenvolvimento', label: 'Desenvolvimento' },
  { value: 'Bugfix', label: 'Correção de Bug' },
  { value: 'Consultoria', label: 'Consultoria' },
  { value: 'Suporte', label: 'Suporte Técnico' },
  { value: 'Design', label: 'Design/UI' },
  { value: 'Infraestrutura', label: 'Infraestrutura' },
  { value: 'Treinamento', label: 'Treinamento' },
  { value: 'Outro', label: 'Outro' },
];

export default function BatchPricingModal({ selectedCount, onClose, onSave }: BatchPricingModalProps) {
  const [mode, setMode] = useState<'individual' | 'total'>('individual');
  const [value, setValue] = useState('');
  const [estimateDays, setEstimateDays] = useState('');
  const [category, setCategory] = useState('Desenvolvimento');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const numValue = parseFloat(value) || 0;
    const numDays = parseFloat(estimateDays) || 0;
    
    onSave({
      value: numValue,
      estimate_days: numDays,
      billing_category: category,
      mode,
    });
  };

  const valuePerItem = mode === 'total' && selectedCount > 0
    ? (parseFloat(value) || 0) / selectedCount
    : parseFloat(value) || 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-slate-800 font-semibold text-base">Precificação em Lote</h2>
            <p className="text-slate-400 text-xs mt-0.5">{selectedCount} item{selectedCount !== 1 ? 's' : ''} selecionado{selectedCount !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Modo de precificação */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">Modo de precificação</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMode('individual')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors cursor-pointer whitespace-nowrap ${
                  mode === 'individual'
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                }`}
              >
                <i className="ri-price-tag-3-line"></i>
                Valor Individual
              </button>
              <button
                type="button"
                onClick={() => setMode('total')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors cursor-pointer whitespace-nowrap ${
                  mode === 'total'
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                }`}
              >
                <i className="ri-stack-line"></i>
                Valor Total
              </button>
            </div>
          </div>

          {/* Valor */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              {mode === 'individual' ? 'Valor por item (R$)' : 'Valor total do lote (R$)'}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">R$</span>
              <input
                type="number"
                min={0}
                step={0.01}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="0,00"
                className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                required
              />
            </div>
            {mode === 'total' && selectedCount > 0 && value && (
              <p className="text-xs text-slate-400 mt-1">
                Cada item receberá {valuePerItem.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </p>
            )}
          </div>

          {/* Prazo estimado */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Prazo estimado (dias)</label>
            <input
              type="number"
              min={0}
              step={0.5}
              value={estimateDays}
              onChange={(e) => setEstimateDays(e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300"
            />
          </div>

          {/* Categoria de cobrança */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Categoria de cobrança</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 cursor-pointer"
            >
              {BILLING_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Preview */}
          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
            <p className="font-medium text-slate-600 mb-1">Resumo da aplicação:</p>
            <ul className="space-y-1">
              <li>• {selectedCount} item{selectedCount !== 1 ? 's' : ''} ser{selectedCount !== 1 ? 'ão' : 'á'} atualizado{selectedCount !== 1 ? 's' : ''}</li>
              <li>• Valor {mode === 'individual' ? 'individual' : 'por item'}: {valuePerItem.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</li>
              <li>• Prazo: {estimateDays || '—'} dia{parseFloat(estimateDays) !== 1 ? 's' : ''}</li>
              <li>• Categoria: {category}</li>
            </ul>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!value || parseFloat(value) <= 0}
            className="px-5 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            Aplicar em {selectedCount} item{selectedCount !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}